<template>
  <div>
    <div v-if="!this.info">
      <div class="no-office">Please click on an office to view its details.</div>
    </div>
    <div
      v-else
      :class="{agency: depth===0, program: depth===1, field: depth===2, branch: depth===3}"
    >
      <div v-if="office" class="d-flex justify-space-between align-items-start">
        <h1 class="left-aligned">
          <span class="type-img"></span>
          {{office.officeName}}
          <span
            v-if="office.officeAbbreviation"
            class="office-abbrev"
          >({{office.officeAbbreviation}})</span>
        </h1>
        <div class="office-type">{{depthName}}</div>
      </div>
      <hr />
      <ul class="property-list">
        <!-- *************** SURVEY STATUS/INFO *************** -->
        <li class="clearfix">
          <p>Survey status:</p>
          <p v-if="surveyRoot">{{ surveyRoot.status }}</p>
        </li>
        <li class="clearfix">
          <p>Lock status:</p>
          <p v-if="surveyRoot" :class="`locked-${this.surveyRoot.isLocked}`">{{ locked }}</p>
        </li>
        <li class="clearfix">
          <p>&nbsp;</p>
          <p>&nbsp;</p>
        </li>
        <li class="clearfix">
          <p>Survey last modified date:</p>
          <p>{{lastEditDate}}</p>
        </li>
        <li class="clearfix">
          <p>Survey last modified by:</p>
          <p v-if="surveyRoot">{{surveyRoot.updateBy}}</p>
        </li>
        <li class="clearfix">
          <p>&nbsp;</p>
          <p>&nbsp;</p>
        </li>
        <!-- SHOW for status = SUBMITTED -->
        <li v-if="wasSubmitted" class="clearfix">
          <p>Submitted date:</p>
          <p>{{lastEditDate}}</p>
        </li>
        <!-- SHOW for status = SUBMITTED -->
        <li v-if="wasSubmitted" class="clearfix">
          <p>Submitted by:</p>
          <p v-if="surveyRoot">{{surveyRoot.updateBy}}</p>
        </li>
        <li class="clearfix">
          <p>Survey due date:</p>
          <p v-if="surveyRoot">{{surveyRoot.surveyDueDate}}</p>
        </li>
        <li class="clearfix">
          <p>&nbsp;</p>
          <p>&nbsp;</p>
        </li>

        <!-- *************** OFFICE HIERARCHY *************** -->
        <!-- LOWEST LEVEL 4:  BRANCH  -->
        <li class="clearfix" v-if="depth >= 3">
          <p>Branch code:</p>
          <p v-if="office" class="office-code">
            <span>{{this.office.agency}}</span>
            <span>{{this.office.program}}</span>
            <span>{{this.office.field}}</span>
            <span>{{this.office.branch}}</span>
          </p>
        </li>

        <!-- LEVEL 3: FIELD OFFICE  -->
        <li class="clearfix" v-if="depth >= 2">
          <p>
            <span
              v-if="depth > 2"
              :class="`depth${fieldDepth}`"
              class="pl-3X"
            >&#10149; In field office:</span>
            <span v-else>Field code:</span>
          </p>
          <p v-if="office">
            <span class="office-code">
              <span>{{this.office.agency}}</span>
              <span>{{this.office.program}}</span>
              <span>{{this.office.field}}</span>
            </span>
            <span v-if="depth > 2"></span>
          </p>
        </li>

        <!-- LEVEL 2: PROGRAM OFFICE  -->
        <li class="clearfix" v-if="depth >= 1">
          <p>
            <span v-if="depth > 1" :class="`depth${programDepth}`">&#10149; In program office:</span>
            <span v-else>Program Office code:</span>
          </p>
          <p v-if="office">
            <span class="office-code">
              <span>{{this.office.agency}}</span>
              <span>{{this.office.program}}</span>
            </span>
            <span v-if="depth > 1"></span>
          </p>
        </li>

        <!-- HIGHEST LEVEL 0: AGENCY -->
        <li class="clearfix">
          <p>
            <span v-if="depth > 0" :class="`depth${agencyDepth}`">&#10149; In agency:</span>
            <span v-else>Agency code:</span>
          </p>
          <p v-if="office">
            <span class="office-code">{{office.agency}}</span>
            <span v-if="depth > 0"></span>
          </p>
        </li>
        <li class="clearfix">
          <p>
            <v-icon medium>account_balance</v-icon>&nbsp;In federal department:
          </p>
          <p v-if="office && office.departmentName">
            {{office.departmentName}}
            <span
              v-if="office && office.departmentAbbreviation"
            >({{office.departmentAbbreviation}})</span>
          </p>
          <p v-else>N/A</p>
        </li>
        <!-- NOT SHOWING -->
        <li class="clearfix hide">
          <p>Retired By:</p>
          <p v-if="office">{{office.retireBy}}</p>
        </li>
        <li class="clearfix hide">
          <p>Retired Date:</p>
          <p v-if="office">{{office.retireDate}}</p>
        </li>
        <li class="clearfix hide">
          <p>Survey Type:</p>
          <p v-if="office">{{office.surveyType}}</p>
        </li>
        <li class="clearfix hide">
          <p>Created By:</p>
          <p v-if="office">{{office.createBy}}</p>
        </li>
        <li class="clearfix hide">
          <p>Created Date:</p>
          <p v-if="office">{{office.createDate}}</p>
        </li>
      </ul>

      <!-- internal R&D IDs (during development) -->
      <div v-if="office && surveyRoot" class="db-info text-right mb-2">
        Office internal ID: {{office.officeId}}
        &nbsp;|&nbsp;
        Survey ID: {{surveyRoot.id}}
        &nbsp;|&nbsp;
        Survey Volume ID: {{surveyRoot.volumeId}}
      </div>

      <!-- Button strip -->
      <div class="d-flex flex-wrap justify-end mt-1 py-1">
        <v-btn @click="onAuthenticateClicked(surveyRootId)" class="primary mb-2" :disabled="!surveyRoot">
          <!-- alt icon: people_alt -->
          <v-icon medium>remove_red_eye</v-icon>&nbsp;View Survey
        </v-btn>
        <v-btn @click="onAuthenticateClicked(surveyRootId)" class="primary mb-2" :disabled="!surveyRoot">
          <v-icon medium>print</v-icon>&nbsp;Print Survey
        </v-btn>
        <v-btn v-if="surveyRoot && surveyRoot.isLocked" class="green darken-3 white--text mb-2" :disabled="!surveyRoot || !surveyRoot.isLocked" @click="onLockSurveyClicked(false,surveyRootId)">
          <v-icon medium>lock_open</v-icon>&nbsp;Unlock Survey
        </v-btn>
        <v-btn v-else class="red darken-3 white--text mb-2" :disabled="!surveyRoot || surveyRoot.isLocked" @click="onLockSurveyClicked(true,surveyRootId)">
          <v-icon medium>lock</v-icon>&nbsp;Lock Survey
        </v-btn>
      </div>

      <div id="peopleInfo" class="my-2">
        <h2>People in this Office</h2>
        <v-flex xs12 class="horiz-margin-20">
          <v-data-table
            :headers="peopleInOfficeHeaders"
            :items="people"
            :single-select="singleSelect"
            item-key="name"
            hide-default-footer
            dense
            class="peopleTable"
            :no-data-text="noUsersText"
          ></v-data-table>
        </v-flex>
      </div>
    </div>
  </div>
</template>

<script>
import router from "@/router";
import { adminDataCollectionService } from "@/services/adminDataCollectionService.js";
import { officeManagementService } from "@/services/officeManagementService.js";
import { authTokenService } from "@/services/authTokenService.js";
import { userService } from "@/services/userService.js";
import { surveyService } from "@/services/surveyService.js";
import { CONSTANTS } from "@/common/constants.js";

export default {
  name: "AdminOfficeDetailsView",
  props: {
    info: null
  },
  data: () => ({
    office: null,
    surveyRoot: null,
    treeNames: null,
    items: [
      { title: CONSTANTS.OFFICE_SUPPORT_ACTIONS.REQUEST_VIEW },
      { title: CONSTANTS.OFFICE_SUPPORT_ACTIONS.REQUEST_ADD }
    ],
    peopleInOfficeHeaders: [
      { text: "First Name", value: "nameFirst" },
      { text: "Last Name", value: "nameLast" },
      { text: "E-mail Address", value: "email" },
			{ text: "Password", value: "showPassword" },
      { text: "Phone Number", value: "phone" },
      { text: "Role", value: "roleId" },
      { text: "Last Login", value: "lastLoginTime" }
    ],
    singleSelect: true,
    noUsersText: 'No users assigned to office',
    people: [],
    surveyRootId: null
  }),
  methods: {
    onAuthenticateClicked (surveyRootId) {
      if (surveyRootId != null) {
        authTokenService.setActiveSurveyId(surveyRootId);
        router.push({ name: "home", params: { previousComponentName: this.$route.name } });
      } else {
        console.log("Impersonation error: null survey ID");
      }
		},
		onSupportItemClicked (item) {
			console.log(item.title);
			if (item.title === CONSTANTS.OFFICE_SUPPORT_ACTIONS.REQUEST_VIEW) {
				router.push({ name: 'admin.supportRequestSearch', params: { previousComponentName: this.$route.name } });
			} else if (item.title === CONSTANTS.OFFICE_SUPPORT_ACTIONS.REQUEST_ADD) {
				router.push({ name: 'admin.supportRequest', params: { previousComponentName: this.$route.name } });
			}
		},
    getOfficeDetails (id) {
      if (id) {
        let me = this;

        adminDataCollectionService
          .getOfficeFromId(id)
          .then(function (resp) {
            me.office = resp.data;
            // me.noUsersText = me.noUsersText + ' ' + me.office.officeName;
            me.noUsersText = "No users are assigned to to office: " + ' ' + me.office.officeName;
          })
          .catch(function (err) {
            console.log(err);
            console.log("Error loading survey status in survey page");
            throw err;
          });
      }
    },
    getSurveyDetails (id) {
      if (id) {
        let me = this;
        console.log("id pansa", id);
        officeManagementService
          .getSurveyByOfficeId(id)
          .then(function (resp) {
            me.surveyRoot = resp.data;
            me.surveyRootId = resp.data.surveyRootId;
            console.log("me.surveyRootId", me.surveyRootId);
          })
          .catch(function (err) {
            console.log(err);
            console.log("Error loading survey status in survey page");
            throw err;
          });
      }
    },
    getOfficeUsers (officeId) {
        let me = this;
        userService.getUsersForOffice(officeId)
        .then(function (resp) {
            me.people = resp.data;
        })
        .catch(function (err) {
            console.log(err);
            console.log("Error loading survey status in survey page");
            throw err;
        });
    },
    onLockSurveyClicked (bLock,surveyRootId) {
      let me = this;
      console.log(bLock !== me.surveyRoot.isLocked);
      if (bLock !== me.surveyRoot.isLocked)
      {
        surveyService.setSurveyLockStatus(surveyRootId, bLock)
        .then(function (resp) {
          if (resp.status === CONSTANTS.HTTP.SUCCESS) {
            me.surveyRoot.isLocked = bLock;
          }
        })
        .catch(function (err) {
              console.log(err);
              console.log("Error attempting to lock survey");
              throw err;
        });
      }
    }
  },
  watch: {
    info: function (newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        // this.treeNames = officeNodeService.getNodeTreeNames(newVal.officeId);
        this.getOfficeDetails(newVal.officeId);
        this.getSurveyDetails(newVal.officeId);
        this.getOfficeUsers(newVal.officeId);
      }
    }
  },
  computed: {
    wasSubmitted: function () {
      if (!this.surveyRoot || !this.surveyRoot.status) return false;
      return (this.surveyRoot.status.toLowerCase() === CONSTANTS.SURVEY_STATUS.COMPLETE.toLowerCase());
    },
    // Returns office node depth in tree
    depth: function () {
      if (!this.office) return 0;

      const office = this.office;
      const emptyOfficeCode = "00000";
      if (office.branch !== emptyOfficeCode) {
        return 3;
      } else if (office.field !== emptyOfficeCode) {
        return 2;
      } else if (office.program !== emptyOfficeCode) {
        return 1;
      } else {
        return 0;
      }
    },
    agencyDepth: function () {
      return this.depth;
    },
    programDepth: function () {
      return this.depth - 1;
    },
    fieldDepth: function () {
      return this.depth - 2;
    },
    locked: function () {
      return this.surveyRoot.isLocked ? "Locked" : "Unlocked";
    },
    depthName: function () {
      let name;
      switch (this.depth) {
        case 0:
          name = "Agency";
          break;
        case 1:
          return "Program Office";
          break;
        case 2:
          name = "Field Office";
          break;
        case 3:
          name = "Branch";
          break;
        default:
          name = "Unknown office type (error)";
          break;
      }
      return name;
    },
    officeCode: function () {
      let oCode;
      const office = this.office;
      switch (this.depth) {
        case 0:
          oCode = office.agency;
          break;
        case 1:
          oCode = office.agency + office.program;
          break;
        case 2:
          oCode = office.agency + office.program + office.field;
          break;
        case 3:
          oCode = office.agency + office.program + office.field + office.branch;
          break;
        default:
          oCode = "Unknown code (error)";
          break;
      }
      return oCode;
    },
    lastEditDate: function () {
      if (this.surveyRoot && this.surveyRoot.updateDate) {
        let date = new Date(this.surveyRoot.updateDate);
        console.log('Last edit date is: ' + date);
        return date;
      } else {
        return "(not started)";
      }
    }
  }
};
</script>

<style scoped>
.left-aligned {
  text-align: left;
}
.property-list {
  border-left: 1px solid lightgray;
  border-right: 1px solid lightgray;
  border-bottom: 1px solid lightgray;
  list-style: none;
  padding: 0;
  margin-bottom: 5px;
}
.property-list > li {
  /* border-top: 1px solid lightgray; */
  padding: 2px;
}
.property-list > li > p {
  margin: 0;
  float: left;
  /* width: 50%; */
  text-align: left;
  padding-left: 15px;
}

.property-list > li > p:first-child {
  border-right: 2px solid ghostwhite;
  width: 30%;
  min-width: 200px;
}

.property-list > li > p:last-child {
  font-weight: bold;
}

h1::before {
  background-image: url(../../assets/data.png);
  background-size: 25px;
  background-repeat: no-repeat;
}

h1 .type-img {
  background-size: 30px;
  background-repeat: no-repeat;
  display: inline-block;
  width: 30px;
  height: 30px;
  vertical-align: bottom;
}

.agency h1 .type-img {
  background-image: url(../../assets/agency.png);
}
.program h1 .type-img {
  background-image: url(../../assets/program.png);
}
.field h1 .type-img {
  background-image: url(../../assets/field.png);
}
.branch h1 .type-img {
  background-image: url(../../assets/branch.png);
}

/* AGENCY = ORANGE */
.agency ul.property-list li:nth-of-type(odd) {
  /* background-color: #fff0eb; */
  /* background-color: #FFE0B2; */
  background-color: #ffccbc;
}
.agency ul.property-list li:nth-of-type(even) {
  /* background-color: #FBE9E7; */
  /* background-color: #FFF3E0; */
  background-color: #fbe9e7;
}

/* PROGRAM = BLUE */
.program ul.property-list li:nth-of-type(odd) {
  /* background-color: #B3E5FC; */
  background-color: #bbdefb;
}
.program ul.property-list li:nth-of-type(even) {
  /* background-color: #E1F5FE; */
  background-color: #e3f2fd;
}

/* FIELD = GREEN */
.field ul.property-list li:nth-of-type(odd) {
  background-color: #c8e6c9;
}
.field ul.property-list li:nth-of-type(even) {
  background-color: #e8f5e9;
}

/* BRANCH = YELLOW */
.branch ul.property-list li:nth-of-type(odd) {
  background-color: #fff9c4;
}
.branch ul.property-list li:nth-of-type(even) {
  background-color: #fffde7;
}

.office-type {
  font-size: 11pt;
  /* color: black; */
  font-style: italic;
  padding: 1px 8px;
  margin-left: 5px;
}

.agency .office-type {
  background-color: #fbe9e7;
  background-color: #ffccbc;
}
.program .office-type {
  background-color: #e3f2fd;
  background-color: #bbdefb;
}
.field .office-type {
  background-color: #e8f5e9;
  background-color: #c8e6c9;
}
.branch .office-type {
  background-color: #fffde7;
  background-color: #fff9c4;
}

.hide {
  display: none;
}

.depth1 {
  padding-left: 10px;
}

.depth2 {
  padding-left: 35px;
}

.depth3 {
  padding-left: 60px;
}

ul.property-list .v-icon {
  color: #2c3e50;
}

.locked-true {
  color: darkred;
}

.office-code {
  margin-right: 10px;
  font-family: "Courier New", Courier, monospace;
  font-weight: normal !important;
}

.office-code span {
  margin-right: 3px;
}

.office-abbrev {
  font-weight: normal;
}

h1 .office-code {
  font-size: 0.9em;
  color: #404040;
}

.db-info {
  font-size: 9pt;
  color: darkgray;
  margin-top: 5px;
}

.peopleTable {
  border: 1px solid gray;
}
</style>
